import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DataStateChangeEvent, GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { IntlService } from '@progress/kendo-angular-intl';
import { CompositeFilterDescriptor, GroupDescriptor, State, filterBy } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Filter } from 'src/app/core/filter/filter';
import { FilterService } from 'src/app/core/filter/filter.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { EventListService } from 'src/app/core/service/grid-data/grid-data-event.service';
import { ReferralTaskListService } from 'src/app/core/service/grid-data/grid-data-referral-task.service';
import { TaskListUtils } from 'src/app/core/service/grid-data/grid-data-task-list-utils.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { TaskUtility } from '../task.utility';
import { ResizedEvent } from 'src/app/shared/resize-event/resized-event';
import { MemberService } from 'src/app/core/service/member.service';
import { PatientEligibilityComponent } from 'src/app/shared/patient-eligibility/patient-eligibility.component';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';

@Component({
  standalone: false,
  selector: 'app-task-list-referral',
  templateUrl: './task-list-referral.component.html',
  styleUrls: ['./task-list-referral.component.scss']
})
export class TaskListReferralComponent implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild(GridComponent) public grid: GridComponent;
  @ViewChild('patientEligibility') public patientEligibility: PatientEligibilityComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public patientLabel = {};
  public breadcrumb = '';
  public currentUser = this.authenticationService.currentUser;
  public currentUserId: number;
  public filterText = '';
  public enableAdvanced = false;
  public filterSelected: Filter;
  public filterDialogOpened = false;
  public stringLiteralBreadcrumb = false;
  public gridContainerWidth = undefined;
  public eligibilityAPIEnabled = false;
  public loadAllReferrals = true; // Workaround for not having server-side group implementation
  public view: GridViewManager;
  public firstLoad = true;

  /**
   * Subscription to Angular component route parameters.
   */
  public routeParamSubscription: Subscription;
  public memberId: number;
  public programId: number;
  public programOpened = false;
  public routerFilter = '';
  public expandedDetailKeys: any[] = [];
  public showDetails = false;
  public selectedTaskKeys = [];
  public distinctPriorities: Array<any> = [];

  /**
   * Referral Task list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  private messagingSubscription: Subscription;
  private listSubscription: Subscription;

  constructor(
    private memberService: MemberService,
    private appConfigService: AppConfigService,
    private referralTaskListService: ReferralTaskListService,
    private messengerService: MessengerService,
    private changeDetectorRef: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private searchHelperService: SearchHelperService,
    private settingService: SettingService,
    private filterService: FilterService,
    private taskUtilityService: TaskUtility,
    private eventListService: EventListService,
    public intl: IntlService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.firstLoad = true;
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.referralTaskListService.loading;
  }

  public ngOnInit(): void
  {
    const user = this.authenticationService.currentUser;
    this.currentUserId = user ? user.id : 0;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.eligibilityAPIEnabled = sessionSetting.eligibilityAPIEnabled || this.memberService.eligibilitySimulationMode() != null;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        const priorities = sessionSetting.taskPriorities.split(',');
        priorities.forEach(p =>
        {
          this.distinctPriorities.push({ priorityName: p, priority: p.toUpperCase() });
        });
      }
    });

    // fetch tasks
    this.routeParamSubscription = this.route.params.subscribe({
      next: (params) =>
      {
        this.filterSelected = undefined;
        this.routerFilter = params.filter;
        this.getTasks(this.routerFilter);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  public ngAfterViewChecked(): void
  {
    // explicit change detection to avoid "expression-has-changed-after-it-was-checked-error"
    this.changeDetectorRef.detectChanges();
  }

  /**
   * This method is to set the gridContainerWidth variable which is passed to the task details UI when none-modal.
   *
   * @param e ResizedEvent
   */
  public onResized(e: ResizedEvent): void
  {
    this.gridContainerWidth = e.newWidth;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    if (this.loadAllReferrals)
    {
      this.view.state.filter = filter;
      this.view.gridData.data = filterBy(this.view.data, filter);
      this.count = this.view.gridData.data.length;
      this.view.refreshView();
    }
    else
    {
      this.state.filter = filter;
      if (this.state.filter.filters.length === 0 && this.routerFilter === 'my')
      {
        this.state.filter.filters = [{ field: 'closedOn', operator: 'isnull', value: '' }];
        this.state.filter.filters.push({ field: 'serviceType', operator: 'isnotnull', value: '' });
      }
      this.runQuery();
    }
  }

  public searchFieldChange(filterText: string): void
  {
    let root: any;
    if (this.loadAllReferrals)
    {
      this.view.state.skip = 0;
      root = this.searchHelperService.fieldChange(filterText, this.view.state, 'caseName');
    }
    else
    {
      this.state.skip = 0;
      root = this.searchHelperService.fieldChange(filterText, this.state, 'caseName');
    }
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    if (this.loadAllReferrals)
    {
      this.view.setState(state);
    }
    else
    {
      this.state = state;
      this.runQuery();
    }
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  public shouldHide(fieldName: string)
  {
    return this.view.groups.some(g => g.field === fieldName);
  }

  public openCommunityServiceInfo(memberId: number, programId: number)
  {
    this.memberId = memberId;
    this.programId = programId;
    this.programOpened = true;
  }

  public closeCommunityServiceInfo()
  {
    this.programOpened = false;
  }

  public expandDetailsBy = (dataItem: any): any => dataItem.id;

  public refresh(): void
  {
    this.state.skip = 0;
    this.getTasks(this.routerFilter);
  }

  public onTaskCompleted(): void
  {
    this.unselectTask();

    // Reset to page 1 if last item of the page has been completed.
    if (this.state.skip > 0 && this.gridView.data.length === 1)
    {
      this.state.skip = 0;
    }

    if (this.loadAllReferrals) 
    {
      this.view.state.skip = this.state.skip;
    }
    this.getTasks(this.routerFilter);
  }

  public checkMedicaidEligibility(memberId: number): void
  {
    this.patientEligibility.execute(memberId);
  }

  /**
   * Unselect any previous selected rows.
   */
  private unselectTask(): void
  {
    this.selectedTaskKeys = [];
    this.collapseAllRows();
  }

  /**
   * Collapse all task details opened.
   */
  private collapseAllRows()
  {
    this.expandedDetailKeys = [];
  }

  private getTasks(filter: string): void
  {
    if (filter === 'active' || filter === 'my' || filter === undefined)
    {
      this.breadcrumb = filter === 'active' ? 'ACTIVE' : filter === 'my' || filter === undefined ? 'MY-OPEN-REFERRALS' : 'ALL';
      this.stringLiteralBreadcrumb = false;

      // hardcoded mechanism for 'my' cases to pick current user's name as manager.
      if (filter === 'active')
      {
        this.state = {
          filter: {
            filters: [
              // this date is meaningless, but we aren't handling empties yet...
              { field: 'closedOn', operator: 'isnull', value: '' },
            ], logic: 'and'
          },
          group: [], skip: 0, sort: [], take: this.appConfigService.getConfig().pageSize
        };
      }
      else if (filter === 'my')
      {
        this.state = {
          filter: {
            filters: [
              { field: 'closedOn', operator: 'isnull', value: '' },
              { field: 'assigneeName', operator: 'eq', value: this.currentUser.name },
            ], logic: 'and'
          }, group: [], skip: 0, sort: [], take: this.appConfigService.getConfig().pageSize
        };
      }
      this.state.filter.filters.push({ field: 'serviceType', operator: 'isnotnull', value: '' });
    }
    else
    {
      this.filterService.getCachedReferralTaskFilters(this.currentUser.userName).subscribe({
        next: (filterList) =>
        {
          const item = filterList.find(obj => obj.id.toString() === filter.toString());
          this.filterSelected = item;
          this.breadcrumb = item && item.name;
          this.stringLiteralBreadcrumb = true;

          this.state = JSON.parse(item.state);
          // reset pagination numbers as we're more concerned with filters and sorting.
          this.state.skip = 0;
          this.state.take = this.appConfigService.getConfig().pageSize;
        }
      });
    }
    this.runQuery();
  }

  private runQuery(): void
  {
    // Update view state
    if (this.loadAllReferrals) 
    {
      this.view.startLoading();
    }
    this.referralTaskListService.queryByState(this.state, (tasks: any) =>
    {
      TaskListUtils.finalizeDataset(tasks, this.taskUtilityService, this.eventListService);

      if (this.loadAllReferrals)
      {
        this.state.filter.filters = [];
        this.count = tasks.total;
        this.view.setData(tasks.data);
        if (this.view.groups.length === 0 && this.firstLoad)
        {
          const group: GroupDescriptor[] = [];
          group.push({ field: 'caseName', dir: 'asc' });
          this.view.setGroup(group);
          this.firstLoad = false;
        }
        this.view.stopLoading();
      }
      else
      {
        this.gridView = {
          data: (tasks) ? this.gridData = tasks.data : this.gridData = [],
          total: (tasks) ? this.count = tasks.total : this.count = 0
        };
      }

      this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
        next: (message) =>
        {
          if (message.type === 'taskDueDateCancelled' || message.type === 'taskDueDateChanged')
          {
            // Implement updates on tasks if neccessary
          }
        }
      });
    }, `&loadall=${this.loadAllReferrals}`);
  }
}
